<template>
    <AuthenticateApp />
</template>

<script lang="ts" setup>
import {defineAsyncComponent, watch,onMounted} from 'vue'

const AuthenticateApp = defineAsyncComponent(() =>
    import("@/AuthenticateApp.vue")
)

import { storeToRefs } from 'pinia'
import { useMainStore, useSnapshotStore, useSlidesStore, useMasterSlidesStore } from '@/store'
import { LOCALSTORAGE_KEY_DISCARDED_DB } from '@/configs/storage'
import { deleteDiscardedDB } from '@/utils/database'
import useFileService from "@/views/Editor/CanvasTool/hooks/useFileService";

const mainStore = useMainStore()
const slidesStore = useSlidesStore()
const masterSlidesStore = useMasterSlidesStore()

const { databaseId } = storeToRefs(mainStore)
const {getFileById} = useFileService()

if (process.env.NODE_ENV === 'production') {
  window.onbeforeunload = () => false
}

onMounted(async () => {
  await deleteDiscardedDB()

  mainStore.setAvailableFonts()
  await storeMasterSlides()
})

const storeMasterSlides = async () => {
  const slides = slidesStore.slides
  for (const slide of slides) {
    if (slide.masterSlideId) {
      const [fetchedSlide] = await getFileById('main', slide.masterSlideId)
      masterSlidesStore.masterSlides.push({ masterSlideId: slide.masterSlideId, slide: fetchedSlide })
    }
  }
}

// When the application logs out, record the database ID of this indexedDB in localStorage, which will be used to clear the database later
window.addEventListener('unload', () => {
  const discardedDB = localStorage.getItem(LOCALSTORAGE_KEY_DISCARDED_DB)
  const discardedDBList: string[] = discardedDB ? JSON.parse(discardedDB) : []

  discardedDBList.push(databaseId.value)

  const newDiscardedDB = JSON.stringify(discardedDBList)
  localStorage.setItem(LOCALSTORAGE_KEY_DISCARDED_DB, newDiscardedDB)
})


</script>

<style lang="scss">

#app {
  background-color: #f9f9f9;
  width: 100%;
  height: 100%;
}

.p-selectbutton .p-button.p-highlight::before {
  background: #ffffff;
  border-color: #ffffff;
  box-shadow: none;
  padding: 0.5rem 0.5rem;
}

.p-selectbutton .p-button.p-highlight,
.p-selectbutton .p-button.p-highlight:hover {
  background: inherit;
  padding: 0.5rem 0.5rem;
}


.p-selectbutton .p-button {
  background: #f1f5f9;
  border: 5px solid #f1f5f9;
  color: #64748b;
  padding: 0.5rem 0.5rem;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s, box-shadow 0.2s, outline-color 0.2s;
}

.first-screen-loading {
  width: 200px;
  height: 200px;
  position: fixed;
  top: 50%;
  left: 50%;
  margin-top: -100px;
  margin-left: -100px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.first-screen-loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #d14424;
  border-top-color: transparent;
  border-radius: 50%;
  animation: spinner .8s linear infinite;
}

.first-screen-loading-text {
  margin-top: 20px;
  color: #d14424;
}

.first-screen-loading-end {
  margin-top: 20px;
  height: 50px;
}

#spinner {
  height: 315px;
  width: 315px;
  animation: loading 12s linear infinite;
}

@keyframes loading {
  to {
    transform: rotate(1turn);
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.7);
  }

  70% {
    box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
  }
}

</style>
